<template>
  <v-main class="business-management">
    <v-container>
      <!-- Loading overlay -->
      <div class="loading-overlay" v-if="loading">
        <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
      </div>
<div class="header">
<div class="header-content">
<h1>
<v-icon color="primary" large>mdi-office-building</v-icon>
Business & Affiliate Management
</h1>

      <!-- Compact stats indicators -->

      <div class="stats-indicators">

        <div class="stat-indicator">

          <v-icon small color="primary">mdi-office-building-outline</v-icon>

          <div class="stat-value">{{ stats.totalBusinessAccounts }}</div>

          <div class="stat-label">Businesses</div>

        </div>

        <div class="stat-indicator">

          <v-icon small color="success">mdi-account-network</v-icon>

          <div class="stat-value">{{ stats.totalAffiliates }}</div>

          <div class="stat-label">Active Affiliates</div>

        </div>

        <div class="stat-indicator">

          <v-icon small color="warning">mdi-cash-multiple</v-icon>

          <div class="stat-value">{{ formatNumber(stats.totalCommissions) }}</div>

          <div class="stat-label">Total Commissions</div>

        </div>

      </div>

    </div>
</div>

      <!-- Commission popup -->
      <v-btn 
        v-if="!commissionsLoaded && !showCommissionPopup" 
        class="commission notification-btn"
        color="primary"
        @click="showCommissionPopup = true"
      >
        <v-icon left>mdi-account-group</v-icon>
        New Sponsorships
      </v-btn>
      <v-btn 
        v-if="commissionsLoaded && !showCommissionPopup" 
        class="commission notification-btn pulse-glow"
        color="primary"
        @click="showCommissionPopup = true"
      >
        <v-icon left>mdi-account-group</v-icon>
        New Sponsorships
      </v-btn>
      <transition name="elegant-appear">
    <v-card 
      v-if="showCommissionPopup" 
      class="commission-popup"
      :class="{ 'pulse-glow': shouldAnimate }"
    >
      <v-card-title class="d-flex justify-space-between align-center">
        <div class="d-flex align-center">
          <v-avatar color="primary" size="40" class="mr-2">
            <v-icon dark>mdi-account-group</v-icon>
          </v-avatar>
          <div>
            <div class="text-h6">New Sponsorships</div>
            <div class="text-caption">Latest affiliate activities & commissions</div>
          </div>
        </div>
        <v-btn icon @click="showCommissionPopup = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider class="my-2"></v-divider>

      <v-card-text style="max-height: 500px; overflow-y: auto" class="py-2">
        <div v-if="recentCommissions.length > 0">
          <v-card 
            v-for="(commission, index) in recentCommissions" 
            :key="index"
            class="mb-3 elevation-3 affiliate-card"
            :class="index % 2 === 0 ? 'blue-light' : 'green-light'"
          >
            <v-card-text>
              <!-- Header with timeline -->
              <div class="d-flex align-center mb-2">
                <v-icon color="primary" class="mr-1">mdi-calendar</v-icon>
                <span class="text-caption">{{ formatDate(commission.date) }}</span>
                <v-spacer></v-spacer>
                <v-chip small :color="commission.status === 'COMPLETED' ? 'green' : 'orange'" dark>
                  <v-icon left small>{{ commission.status === 'COMPLETED' ? 'mdi-check' : 'mdi-clock' }}</v-icon>
                  {{ commission.status === 'COMPLETED' ? 'Verified' : getStatusText(commission.status) }}
                </v-chip>
              </div>

                              <!-- New Affiliate Info (Sponsored User) -->
                <div class="affiliate-info text-center">
                  <v-avatar color="teal" size="50" class="mb-1">
                    <v-icon dark large>mdi-account-plus</v-icon>
                  </v-avatar>
                  <div class="font-weight-bold text-caption">
                    {{ commission.new_affiliate.name || 'Sponsored User' }}
                  </div>
                  <div class="text-caption">@{{ commission.new_affiliate.username || 'N/A' }}</div>
                  <v-chip x-small color="teal" class="mt-1">
                    New member
                  </v-chip>
                  
                  <v-divider class="my-1"></v-divider>
                  
                  <div class="text-center">
                    <div class="text-caption">Registration Value</div>
                    <div class="font-weight-bold teal--text">
                      {{ formatCurrency(commission.new_affiliate.potentialCommission) }}
                    </div>
                  </div>
                </div>
                                <div class="commission-viz">
                  <div class="commission-arrow">
                    <v-icon color="green" large>mdi-arrow-right</v-icon>
                  </div>
                  <div class="commission-amount text-center">
                    <div class="text-caption">Commission</div>
                    <div class="text-h6 success--text">
                      +{{ formatCurrency(commission.amount) }}
                    </div>
                    <v-chip x-small color="green" class="mt-1">
                      {{ (commission.commission_rate * 100) }}% of registration
                    </v-chip>
                  </div>
                </div>
                
              
              <!-- Affiliate connection visualization -->
              <div class="connection-line">
                <div class="connection-dot start">
                  <v-icon small color="primary">mdi-account</v-icon> 
                </div>
                <div class="connection-line-path"></div>
                <div class="connection-dot end">
                  <v-icon small color="teal">mdi-account-plus</v-icon>
                </div>
              </div>
              
              <div class="d-flex justify-space-between flex-wrap">
                <!-- Referrer Info (Commission Receiver) -->
                <div class="referrer-info text-center">
                  <v-avatar color="primary" size="50" class="mb-1">
                    <v-icon dark large>mdi-account</v-icon>
                  </v-avatar>
                  
                  <div class="font-weight-bold text-caption">
                    {{ commission.referrer.name || 'Unknown' }}
                  </div>
                  <div class="text-caption">@{{ commission.referrer.username || 'N/A' }}</div>
                  
                  <v-chip x-small color="primary" class="mt-1">
                    {{ commission.referrer.affiliateCode || 'N/A' }}
                  </v-chip>
                  
                  <v-divider class="my-1"></v-divider>
                  
                  <div class="d-flex justify-center">
                    <v-tooltip top>
                      <template v-slot:activator="{ on, attrs }">
                        <div class="text-center mx-1" v-bind="attrs" v-on="on">
                          <div class="text-caption">Old Balance</div>
                          <div class="font-weight-bold">
                            {{ formatCurrency(commission.old_balance) }}
                          </div>
                        </div>
                      </template>
                      <span>Balance before commission</span>
                    </v-tooltip>
                    
                    <v-tooltip top>
                      <template v-slot:activator="{ on, attrs }">
                        <div class="text-center mx-1" v-bind="attrs" v-on="on">
                          <div class="text-caption">New Balance</div>
                          <div class="font-weight-bold success--text">
                            {{ formatCurrency(commission.new_balance) }}
                          </div>
                        </div>
                      </template>
                      <span>Balance after commission</span>
                    </v-tooltip>
                  </div>
                </div>
                
                <!-- Commission Visualization -->


              </div>
            </v-card-text>
          </v-card>
        </div>
        
        <div v-else class="text-center py-4">
          <v-icon size="64" color="grey lighten-1">mdi-account-off</v-icon>
          <h3 class="subtitle-1 grey--text">No new sponsorships</h3>
          <p class="text-caption">New affiliate registrations will appear here</p>
        </div>
      </v-card-text>
      
      <v-card-actions class="px-2 py-2">
        <v-spacer></v-spacer>
        <v-btn color="primary" text @click="showCommissionPopup = false">
          Close Panel
        </v-btn>
      </v-card-actions>
    </v-card>
  </transition>
  
        <div class="header-actions">
          <v-btn color="primary" @click="fetchData" class="action-btn">
            <v-icon left>mdi-refresh</v-icon>
            Refresh
          </v-btn>
        <v-btn
          outlined
          color="primary"
          class="ml-3 action-btn"
          :to="{ name: 'savings.BusinessSetting' }"
        >
          <v-icon left>mdi-cog</v-icon>
          Settings
        </v-btn>

        </div>
 

      <!-- Tab navigation -->
      <v-tabs v-model="activeTab" background-color="transparent" color="primary" class="mb-6">
        <v-tab>
          <v-icon left>mdi-store</v-icon>
          Businesses
        </v-tab>

        <v-tab>
          <v-icon left>mdi-chart-bar</v-icon>
          Analytics
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab">
        <!-- Businesses Tab -->
        <v-tab-item>
          <v-row>
            <v-col cols="12">
              <v-card class="dashboard-card">
                <div class="card-header">
                  <div class="card-title">
                    <v-icon color="primary">mdi-format-list-bulleted</v-icon>
                    Business Accounts
                  </div>
                  <div>
                    <v-text-field
                      v-model="searchQuery"
                      placeholder="Search businesses..."
                      outlined
                      dense
                      hide-details
                      prepend-inner-icon="mdi-magnify"
                      class="search-field"
                    ></v-text-field>
                    <v-btn outlined color="primary" class="ml-3">
                      <v-icon left>mdi-download</v-icon>
                      Export
                    </v-btn>
  
                  </div>
                </div>
                
                <v-data-table
                  :headers="businessHeaders"
                  :items="filteredBusinessAccounts"
                  hide-default-footer
                  class="elevation-0 custom-table"
                >
                  <template v-slot:item.user="{ item }">
                    <div class="user-info">
                      <div class="avatar">{{ item.name.charAt(0) }}</div>
                      <div>
                        <div class="user-name">{{ item.name }}</div>
                        <div class="user-email">{{ item.email }}</div>
                      </div>
                    </div>
                  </template>
                  
                  <template v-slot:item.status="{ item }">
                    <v-chip small :color="item.status === 'active' ? 'success' : 'warning'" dark>
                      {{ item.status === 'active' ? 'Active' : 'Pending' }}
                    </v-chip>
                  </template>
                  
                  <template v-slot:item.commissions="{ item }">
                    <div class="commission-value">
                      <v-icon small >mdi-currency-usd</v-icon>
                      {{ formatNumber(item.commissions) }} FCFA
                    </div>
                  </template>
                  
                  <template v-slot:item.actions="{ item }">
                    <div class="actions">
                      <!-- View Details Button -->
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                          <v-btn icon v-on="on" @click="openUserDetails(item.id)" color="Primary">
                            <v-icon small>mdi-eye-outline</v-icon>
                          </v-btn>
                        </template>
                        <span>View Details</span>
                      </v-tooltip>
                      
                      <!-- Reverse Commission Button -->
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                          <v-btn icon v-on="on" @click="openReverseCommission(item.id)" color="deep-orange">
                            <v-icon small>mdi-undo</v-icon>
                          </v-btn>
                        </template>
                        <span>Reverse Commission</span>
                      </v-tooltip>
                    </div>
                  </template>
                  
                  <template v-slot:no-data>
                    <div class="empty-state">
                      <v-icon large >mdi-account-off-outline</v-icon>
                      <h3>No businesses found</h3>
                      <p>Try adjusting your search criteria</p>
                    </div>
                  </template>
                </v-data-table>
              </v-card>
            </v-col>
          </v-row>
        </v-tab-item>

        <!-- Affiliate Network Tab -->
      
        
        <!-- Analytics Tab -->
      <v-tab-item>
  <v-row>
    <v-col cols="12" md="8">
      <v-card class="dashboard-card">
        <div class="card-header">
          <div class="card-title">
            <v-icon color="primary">mdi-chart-bar</v-icon>
            Commission Analytics (3025 FCFA)
          </div>
          <v-select
            v-model="selectedPeriod"
            :items="periodOptions"
            label="Time Period"
            outlined
            dense
            hide-details
            class="period-select"
          ></v-select>
        </div>
        
        <div class="chart-container">
          <div v-if="commissionData.length > 0" class="chart-placeholder">
            <div class="chart-header">
              <h3>Commission Trends</h3>
              <div class="chart-legend">
                <div class="legend-item">
                  <div class="legend-color" style="background-color: #4e54c8;"></div>
                  <span>Commission (3025 FCFA)</span>
                </div>
              </div>
            </div>
            
          
            <div class="scrollable-container"> 
              <div class="chart-bars">
                <div v-for="(item, index) in commissionData" :key="index" class="chart-bar">
                  <div class="bar-label">{{ item.label }}</div>
                  <div class="bar-container">
                    <div 
                      class="bar-value" 
                      :style="{ height: `${(item.value / maxCommissionValue) * 100}%`, backgroundColor: '#4e54c8' }"
                    ></div>
                  </div>
                  <div class="bar-amount">{{ formatNumber(item.value) }} FCFA</div>
                </div>
              </div>
            </div>
            
            
          </div>
          <div v-else class="empty-chart">
            <v-icon large color="lightGray">mdi-chart-bar</v-icon>
            <h3>Commission Statistics</h3>
            <p>Visualize trends and distribution</p>
          </div>
        </div>
      </v-card>
    </v-col>
            
            <v-col cols="12" md="4">
              <v-card class="dashboard-card">
                <div class="card-header">
                  <div class="card-title">
                    <v-icon color="primary">mdi-trophy</v-icon>
                    Top Affiliates (3025 FCFA)
                  </div>
                </div>
                
                <div class="top-affiliates-list">
                  <div v-for="(affiliate, index) in topAffiliates" :key="affiliate.id" class="affiliate-item">
                    <div class="affiliate-rank">
                      <v-chip small color="primary">{{ index + 1 }}</v-chip>
                    </div>
                    <div class="user-info">
                      <div class="avatar">{{ affiliate.name.charAt(0) }}</div>
                      <div>
                        <div class="user-name">{{ affiliate.name }}</div>
                        <div class="user-email">{{ affiliate.email }}</div>
                      </div>

                    </div>
                    <div class="affiliate-commission">
                      <v-icon small color="warning"> fa-money-bill-wave</v-icon>
                      {{ formatNumber(affiliate.commissions) }} F
                    </div>
                  </div>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </v-tab-item>
      </v-tabs-items>


      <!-- User Details Dialog -->
      <v-dialog v-model="showUserDetails" max-width="800">
        <v-card v-if="selectedUser">
          <div class="card-header">
            <div class="card-title">Details: {{ selectedUser.name }}</div>
            <v-btn icon @click="showUserDetails = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
          
          <v-card-text>
            <v-row>
              <v-col cols="12" md="6">
                <v-card class="detail-card">
                  <div class="detail-label">Account Status</div>
                  <div class="detail-value">
                    <v-chip small :color="selectedUser.status === 'active' ? 'success' : 'warning'" dark>
                      {{ selectedUser.status === 'active' ? 'Active' : 'Pending' }}
                    </v-chip>
                  </div>
                </v-card>
                
                <v-card class="detail-card">
                  <div class="detail-label">Email</div>
                  <div class="detail-value">{{ selectedUser.email }}</div>
                </v-card>
                
                <v-card class="detail-card">
                  <div class="detail-label">Phone</div>
                  <div class="detail-value">{{ selectedUser.phone || 'N/A' }}</div>
                </v-card>
              </v-col>
              
              <v-col cols="12" md="6">
                <v-card class="detail-card">
                  <div class="detail-label">Balance</div>
                  <div class="detail-value">{{ formatNumber(selectedUser.balance) }} XOF</div>
                </v-card>
                
                <v-card class="detail-card">
                  <div class="detail-label">Total_Commissions</div>
                  <div class="detail-value">{{ formatNumber(selectedUser.commissions) }} XOF</div>
                </v-card>
                
                <v-card class="detail-card">
                  <div class="detail-label">Affiliates</div>
                  <div class="detail-value">{{ selectedUser.affiliateCount }}</div>
                </v-card>
              </v-col>
            </v-row>
            
            <div v-if="selectedUser.referrer">
              <h3 class="section-title">
                <v-icon>mdi-account-arrow-left</v-icon>
                Referrer
              </h3>
              
              <v-card class="referrer-card">
                <div class="user-info">
                  <div class="avatar">{{ selectedUser.referrer.name.charAt(0) }}</div>
                  <div>
                    <div>{{ selectedUser.referrer.name }}</div>
                    <div class="user-id">
                      ID: {{ selectedUser.referrer.serialNo }} | Affiliates: {{ selectedUser.referrer.affiliateCount }}
                    </div>
                  </div>
                </div>
              </v-card>
            </div>
            
            <h3 class="section-title">
              <v-icon>mdi-account-group</v-icon>
              Affiliates ({{ selectedUser.affiliates.length }})
            </h3>
            
            <div class="referral-list">
              <div v-for="affiliate in selectedUser.affiliates" :key="affiliate.id" class="referral-item">
                <div class="user-info">
                  <div class="avatar">{{ affiliate.name.charAt(0) }}</div>
                  <div>
                    <div class="user-name">{{ affiliate.name }}</div>
                    <div class="user-email">{{ affiliate.email }}</div>
                  </div>
                </div>
                
                <div class="affiliate-stats">
                  <div>
                    <div class="detail-label">Status</div>
                    <v-chip small :color="affiliate.status === 'active' ? 'success' : 'warning'" dark>
                      {{ affiliate.status === 'active' ? 'Active' : 'Pending' }}
                    </v-chip>
                  </div>
                  
                  <div>
                    <div class="detail-label">Commission</div>
                    <div>{{ formatNumber(affiliate.commission) }} FCFA</div>
                  </div>
                </div>
              </div>
            </div>
            
           <div v-if="selectedUser.commissionHistory.length > 0">
            <h3 class="section-title">
              <v-icon>mdi-history</v-icon>
              Historique des Commissions ({{ formatNumber(selectedUser.commissionHistory.reduce((sum, c) => sum + c.amount, 0)) }} XOF)
            </h3>
            
            <v-card 
              v-for="(commission, index) in selectedUser.commissionHistory" 
              :key="index" 
              class="commission-item"
            >
              <div class="commission-info">
                <div class="commission-description">
                  <div>{{ commission.description || `Commission #${commission.id}` }}</div>
                  <div class="affiliate-info">
                    <v-icon small>mdi-account</v-icon>
                    {{ commission.affiliateName }} (ID: {{ commission.affiliateSerialNo }})
                  </div>
                </div>
                <div class="commission-date">{{ formatDate(commission.date) }}</div>
              </div>
              <div class="commission-amount">
                +{{ formatNumber(commission.amount) }} XOF
              </div>
            </v-card>
          </div>
          </v-card-text>
          
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn outlined color="primary" @click="showUserDetails = false">Close</v-btn>
            <v-btn color="primary" @click="sendMessage(selectedUser)">
              <v-icon left>mdi-check</v-icon>
              ok
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

 <v-dialog v-model="showReverseCommission" max-width="800">
  <v-card v-if="selectedUser">
    <div class="card-header">
      <div class="card-title">
        Reverse Commission - {{ selectedUser.name }}
      </div>
      <v-btn icon @click="showReverseCommission = false">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </div>
    
    <v-card-text>
      <!-- Commission Selection Section -->
      <v-card class="commission-selection">
        <div class="selection-header">
          <v-icon color="primary">mdi-format-list-bulleted</v-icon>
          Select Commission to Reverse
        </div>
        
        <v-data-table
          :headers="commissionHeaders"
          :items="selectedUser.commissionHistory"
          item-key="id"
          hide-default-footer
          class="elevation-0 custom-table"
        >
          <template v-slot:item.date="{ item }">
            {{ formatDate(item.date) }}
          </template>
          
          <!-- Nouveau template pour l'affilié -->

      <template v-slot:item.affiliate="{ item }">
          <div v-if="item.user ">
              <div>{{ item.user.name }}</div>
              <div class="text-caption text-grey">
                  {{ item.user.affiliateCode || 'No code' }}
              </div>
          </div>
          <div v-else>
              <div v-if="item.description && item.description.includes('sponsorship')">
                  {{ item.affiliateName || 'Unknown' }}  <!-- Utiliser le nom de l'affilié extrait -->
              </div>
              <div v-else class="text-grey">
                  Unknown
              </div>
          </div>
      </template>
          
          <template v-slot:item.amount="{ item }">
            <div class="commission-value">
              <v-icon small>mdi-currency-usd</v-icon>
              {{ formatNumber(item.amount) }} FCFA
            </div>
          </template>
          
          <template v-slot:item.select="{ item }">
            <v-radio-group v-model="selectedCommission" hide-details>
              <v-radio :value="item"></v-radio>
            </v-radio-group>
          </template>
        </v-data-table>
      </v-card>

      <!-- Reversal Details Section -->
      <div v-if="selectedCommission" class="reversal-details mt-6">
        <v-card class="reversal-alert">
          <div class="alert-content">
            <v-icon color="error">mdi-alert-circle</v-icon>
            <div>
              <div>Reversing commission from {{ formatDate(selectedCommission.date) }}</div>
              <div class="reversal-amount">
                Amount: {{ formatNumber(selectedCommission.amount) }} FCFA
              </div>
              
              <!-- Nouveau: Affichage des détails de l'affilié -->
              <div v-if="selectedCommission.user && selectedCommission.user.id" class="mt-2">
                <v-divider class="my-2"></v-divider>
                <div class="affiliate-info">
                  <v-icon small>mdi-account</v-icon>
                  Affilié: {{ selectedCommission.user.name }}
                </div>
                <div class="affiliate-info">
                  <v-icon small>mdi-identifier</v-icon>
                  Code: {{ selectedCommission.user.affiliateCode || 'N/A' }}
                </div>
              </div>
            </div>
          </div>
        </v-card>
        
        <v-form class="mt-4">
  <v-select
    v-model="reversalReason"
    :items="reversalReasons"
    label="Reversal Reason"
    outlined
    dense
    required
    :rules="[v => !!v || 'Reason is required']"
    class="mb-3"
    menu-props="auto, offsetY"
  >
    <!-- Slot personnalisé pour contrôler l'affichage des items -->
    <template v-slot:item="{ item }">
      <v-list-item-content class="py-1">
        <v-list-item-title style="white-space: normal; line-height: 1.4">
          {{ item.text }}
        </v-list-item-title>
      </v-list-item-content>
    </template>
    
    <!-- Slot pour l'affichage de la sélection -->
    <template v-slot:selection="{ item }">
      <span style="white-space: normal; line-height: 1.3">
        {{ item.text }}
      </span>
    </template>
  </v-select>
  
  <!-- Les textareas restent inchangés -->
  <v-textarea
    v-model="reversalExplanation"
    label="Detailed Explanation"
    outlined
    dense
    placeholder="Explain why this commission is being reversed..."
    required
    :rules="[v => !!v || 'Explanation is required']"
    class="mb-3"
    auto-grow
    rows="2"
  ></v-textarea>
  
  <v-textarea
    v-model="reversalEmail"
    label="Email Notification"
    outlined
    dense
    placeholder="Message to send to the user..."
    class="mb-3"
    auto-grow
    rows="2"
  ></v-textarea>
</v-form>
        </div>
      </v-card-text>
      
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn outlined color="primary" @click="showReverseCommission = false">Cancel</v-btn>
        <v-btn 
          color="error" 
          @click="confirmReversal"
          :disabled="!selectedCommission"
          :loading="reversalLoading"
        >
          <v-icon left>mdi-undo</v-icon>
          Reverse Commission
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from 'axios';

export default {
  data() {
    return {
      activeTab: 0,
      searchQuery: '',
      affiliateSearch: '',
      selectedPeriod: 'month',
      filteredAffiliates: [],
      periodOptions: [
        { text: 'Last 7 Days', value: 'week' },
        { text: 'Last 30 Days', value: 'month' },
        { text: 'Last 90 Days', value: 'quarter' },
        { text: 'Last Year', value: 'year' }
      ],
      recentCommissions: [],
      commissionsLoaded: false,
      showCommissionPopup: false,
      shouldAnimate: false,
      closeTimer: null,
      apparitionTimer: null,
      animationTimer: null,
      selectedUser: null,
      commissionToReverse: null,
      showUserDetails: false,
      showReverseCommission: false,
     
      selectedCommission: null,
      reversalReason: 'duplicate',
      reversalExplanation: '',
      reversalEmail: '',
      reversalLoading: false,
reversalReasons: [
  { 
    text: 'Duplicate transaction (correct commission attribution)', 
    value: 'duplicate'
  },
  { 
    text: 'Incorrect commission (cancels sponsorship)', 
    value: 'error'
  },
  { 
    text: 'Suspected fraud (cancels sponsorship)', 
    value: 'fraud'
  },
  { 
    text: 'Other reason (cancels sponsorship)', 
    value: 'other'
  }
],
      commissionHeaders: [
        { text: 'Date', value: 'date' },
        { text: 'Affilié', value: 'affiliate' },  
        { text: 'Amount', value: 'amount' },
        { text: 'Select', value: 'select', align: 'center', sortable: false }
      ],
      loading: false,
      stats: {
        totalBusinessAccounts: 0,
        totalAffiliates: 0,
        totalCommissions: 0
      },
      businessAccounts: [],
      serialProfiles: [],
      topAffiliates: [],
      commissionData: [],
      businessHeaders: [
        { text: 'Business', value: 'username', width: '30%' },
        { text: 'Status', value: 'payment_status', width: '15%' },
        { text: 'Balance', value: 'balance', width: '15%' },
        { text: 'Transaction ID', value: 'transaction_id', width: '15%' },
        { text: 'Created At', value: 'created_at', width: '15%' },
        { text: 'Actions', value: 'actions', sortable: false, width: '10%' }
      ],
      affiliateHeaders: [
        { text: 'Serial No', value: 'serial_no', width: '20%' },
        { text: 'Affiliate', value: 'referrer_username', width: '30%' },
        { text: 'Validated', value: 'validated_affiliates', width: '15%' },
        { text: 'Commissions', value: 'total_commissions', width: '20%' },
        { text: 'Actions', value: 'actions', sortable: false, width: '15%' }
      ],
      showAddCommission: false,
      newCommission: {
        business: null,
        amount: '3025',
        description: '',
        poster: null
      },
      posterPreview: null
    };
  },
  async mounted() {

    
    
    this.apparitionTimer = setTimeout(() => {
      this.showCommissionPopup = true;
      this.animationTimer = setTimeout(() => {
        this.shouldAnimate = true;
      }, 5000);
      
      this.closeTimer = setTimeout(() => {
        this.showCommissionPopup = false;
      },10000);
    }, 3000);
    await this.fetchData();
   
  },
  beforeDestroy() {
    clearTimeout(this.apparitionTimer);
    clearTimeout(this.animationTimer);
    clearTimeout(this.closeTimer);
  },
  computed: {
    filteredBusinessAccounts() {
      return this.businessAccounts.filter(business => 
        business.username.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
        business.email.toLowerCase().includes(this.searchQuery.toLowerCase())
      );
    },
    filteredSerialProfiles() {
      return this.serialProfiles.filter(profile => 
        profile.referrer_username.toLowerCase().includes(this.affiliateSearch.toLowerCase()) ||
        profile.serial_no.toLowerCase().includes(this.affiliateSearch.toLowerCase())
      );
    },
    maxCommissionValue() {
      if (this.commissionData.length === 0) return 100;
      return Math.max(...this.commissionData.map(item => item.value));
    }
  },
  methods: {
    closePopup() {
      this.showCommissionPopup = false;
      if (this.timeoutId) {
        clearTimeout(this.timeoutId);
      }
    },
async fetchData() {
  try {
    const endpoints = [
      '/api/v1/manager/business-dashboard/',
      '/api/v1/manager/business-accounts2/',
      '/api/v1/manager/affiliate-management/',
      '/api/v1/manager/recent-commissions/'
    ];

    const responses = await Promise.allSettled(endpoints.map(url => axios.get(url)));
    
    // Traitement individualisé des réponses
    responses.forEach((res, index) => {
      if (res.status === 'fulfilled') {
        switch(index) {
          case 0: 
            this.stats = res.value.data.stats;
            this.topAffiliates = res.value.data.topAffiliates;
            this.commissionData = res.value.data.commissionData;
            break;
          case 1:
            this.businessAccounts = res.value.data.business_profiles;
            break;
          case 2:
            this.serialProfiles = res.value.data.serial_profiles;
            break;
          case 3:
            this.recentCommissions = Array.isArray(res.value.data) 
              ? res.value.data 
              : [res.value.data];
            break;
        }
      } else {
        console.error(`Erreur endpoint ${endpoints[index]}`, res.reason);
      }
    });
  } catch (error) {
    console.error('Erreur générale', error);
  }
},
  formatDate(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  },
formatCurrency(amount) {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'XOF'
  }).format(amount).replace('XOF', 'FCFA');
},


    
async openUserDetails(userId) {
  try {
    const response = await axios.get(`/api/v1/manager/user-details/${userId}/`);
    const data = response.data;

    this.selectedUser = {
      ...data,
      name: data.username,
      serialNo: data.serial_no,
      commissionHistory: data.commission_history?.map(item => ({
        ...item,
        description: item.description || `Commission #${item.id}`
      })) || []
    };

    this.showUserDetails = true;
  } catch (error) {
    this.showError('Error loading user details: ' + error.message);
  }
},
async openReverseCommission(userId) {
    try {
        const response = await axios.get(`/api/v1/manager/user-details/${userId}/`);
        
        this.selectedUser  = {
            ...response.data,
            name: response.data.username,
            commissionHistory: response.data.commission_history.map(commission => ({
                ...commission,
                user: commission.user || {
                    id: null,
                    name: commission.description?.split(' ')[-1] || 'Unknown',  // Extraire le nom de l'affilié
                    affiliateCode: 'N/A'
                }
            }))
        };
        
        this.showReverseCommission = true;
        this.selectedCommission = null;
        this.reversalReason = 'error';
        this.reversalExplanation = '';
        this.reversalEmail = '';
    } catch (error) {
        console.error('Error loading user details:', error);
        this.showError('Erreur lors du chargement des détails: ' + error.message);
    }
},
 
   async confirmReversal() {
  if (!this.selectedCommission) return;
  
  this.reversalLoading = true;
  try {
    const payload = {
      commission_id: this.selectedCommission.id,
      reason: this.reversalReason || "Raison non spécifiée",
      explanation: this.reversalExplanation || "",
      email_message: this.reversalEmail || ""
    };

    const response = await axios.post(
      '/api/v1/manager/reverse-commission/',
      payload,
      {

      }
    );
    
    console.log('Commission reversed successfully:', response.data);
    this.showSuccess('Commission reversed successfully');
    this.showReverseCommission = false;
    this.fetchData();
    
  } catch (error) {
    console.error('Error reversing commission:', error);
    // Afficher l'erreur spécifique
    if (error.response) {
      console.error('Response data:', error.response.data);
      this.showError(error.response.data.error || 'Erreur inconnue');
    }
  } finally {
    this.reversalLoading = false;
  }
},

generateEmailTemplate(user, amount) {
  return `Dear ${user.name || user.username},

Commission reversal details:
- Amount: ${this.formatNumber(amount)} FCFA
- Reason: ${this.reversalReason || '[Reason]'}
- Details: ${this.reversalExplanation || '[Details]'}

Sincerely,
The Management Team`;
},

resetReversalForm() {
  this.reversalReason = 'error';
  this.reversalExplanation = '';
  this.reversalEmail = '';
  this.commissionToReverse = null;
},
    sendMessage(user) {
      this.showSuccess(`you have view the details of the user  ${user.username}`);
      this.showUserDetails = false;
    },
    
    formatNumber(num) {
      return num?.toLocaleString('en-US') || '0';
    },
    

    

    
    showSuccess(message) {
      this.$store.commit('setSnackBarMessage', message);
      this.$store.commit('setSnackBarColor', "success");
      this.$store.commit('activateSnackBar', true);
    },
    
    showError(message) {
      this.$store.commit('setSnackBarMessage', message);
      this.$store.commit('setSnackBarColor', "error");
      this.$store.commit('activateSnackBar', true);
    },
    
    openAddCommission() {
      this.showAddCommission = true;
    },
    
    closeAddCommission() {
      this.showAddCommission = false;
      this.resetCommissionForm();
    },
    
    resetCommissionForm() {
      this.newCommission = {
        business: null,
        amount: '3025',
        description: '',
        poster: null
      };
      this.posterPreview = null;
      this.$refs.commissionForm?.resetValidation();
    },



    
    async saveCommission() {
      if (this.$refs.commissionForm.validate()) {
        this.loading = true;
        try {
          const formData = new FormData();
          formData.append('business_id', this.newCommission.business);
          formData.append('amount', this.newCommission.amount);
          formData.append('description', this.newCommission.description);
          
          if (this.newCommission.poster) {
            formData.append('poster', this.newCommission.poster);
          }
          
          await axios.post('/api/add-commission/', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
          
          this.showSuccess('Commission added successfully');
          this.closeAddCommission();
          this.fetchData(); // Refresh data
        } catch (error) {
          this.showError('Error adding commission: ' + error.message);
        } finally {
          this.loading = false;
        }
      }
    }
  },
  watch: {
    'newCommission.poster'(file) {
      if (file) {
        const reader = new FileReader();
        reader.onload = e => {
          this.posterPreview = e.target.result;
        };
        reader.readAsDataURL(file);
      } else {
        this.posterPreview = null;
      }
    }
  }
};
</script>


<style scoped>
.business-management {
  --primary: #4e54c8;
  --secondary: #8f94fb;
  --success: #38c172;
  --warning: #FF9800;
  --danger: #F44336;
  --teal: #009688;  /* New color for View Details */
  --deep-orange: #FF5722; /* New color for Reverse Commission */
  --light: #f8f9fa;
  --dark: #343a40;
  --gray: #6c757d;
  --light-gray: #e9ecef;
  --border: #e0e0e0;
  --card-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  --transition: all 0.3s ease;

  font-family: 'Poppins', sans-serif;
  background-color: #f5f7fa;
  min-height: 100vh;
  padding: 20px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  flex-wrap: wrap;
}

.header-content {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
  flex-grow: 1;
}

.header h1 {
  color: var(--dark);
  font-weight: 600;
  font-size: 1.8rem;
  display: flex;
  align-items: center;
  gap: 12px;
  margin-right: 30px;
}

.stats-indicators {
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
}

.stat-indicator {
  background: white;
  border-radius: 12px;
  box-shadow: var(--card-shadow);
  padding: 12px 18px;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 120px;
  transition: var(--transition);
}

.stat-indicator:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
}

.stat-value {
  font-size: 1.6rem;
  font-weight: 600;
  margin: 5px 0;
}

.stat-label {
  color: var(--gray);
  font-size: 0.85rem;
  text-align: center;
}

.header-actions {
  display: flex;
  gap: 12px;
  margin-top: 15px;
}

.action-btn {
  text-transform: none;
  letter-spacing: normal;
  font-weight: 500;
}

.dashboard-card {
  background: white;
  border-radius: 16px;
  box-shadow: var(--card-shadow);
  padding: 25px;
  margin-bottom: 25px;
  transition: var(--transition);
}

.dashboard-card:hover {
  box-shadow: 0 6px 25px rgba(0, 0, 0, 0.12);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 15px;
}

.card-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--dark);
  display: flex;
  align-items: center;
  gap: 10px;
}

.search-field {
  max-width: 300px;
}

.custom-table {
  border-radius: 12px;
  overflow: hidden;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--secondary);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 1rem;
}

.user-name {
  font-weight: 500;
}

.user-email, .user-id {
  font-size: 0.85rem;
  color: var(--gray);
}

.commission-value {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
}

.actions {
  display: flex;
  gap: 8px;
}

.empty-state {
  text-align: center;
  padding: 40px;
  color: var(--gray);
}

.empty-state h3 {
  font-weight: 500;
  margin: 15px 0 5px;
}

.empty-state p {
  font-size: 0.95rem;
}

.chart-container {
  height: 350px;
  padding: 15px;
}

.chart-placeholder {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
}

.chart-legend {
  display: flex;
  gap: 15px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 5px;
}

.legend-color {
  width: 12px;
  height: 12px;
  border-radius: 3px;
}

.chart-bars {
  display: flex;
  height: 100%;
  align-items: flex-end;
  gap: 25px;
  padding: 0 20px;
}

.chart-bar {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
}

.bar-label {
  margin-bottom: 10px;
  font-size: 0.9rem;
}

.bar-container {
  flex: 1;
  width: 30px;
  background: var(--light-gray);
  border-radius: 4px 4px 0 0;
  display: flex;
  align-items: flex-end;
}

.bar-value {
  width: 100%;
  transition: height 0.5s ease;
  border-radius: 4px 4px 0 0;
}

.bar-amount {
  margin-top: 8px;
  font-weight: 500;
  font-size: 0.9rem;
}

.empty-chart {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: var(--gray);
}

.top-affiliates-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.affiliate-item {
  display: flex;
  align-items: center;
  padding: 12px;
  border-bottom: 1px solid var(--border);
}

.affiliate-item:last-child {
  border-bottom: none;
}

.affiliate-rank {
  margin-right: 15px;
}

.affiliate-commission {
  margin-left: auto;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 5px;
}

.detail-card {
  background: var(--light);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
}

.detail-label {
  font-size: 0.9rem;
  color: var(--gray);
  margin-bottom: 5px;
}

.detail-value {
  font-size: 1.1rem;
  font-weight: 500;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 25px 0 15px;
  color: var(--dark);
}

.referrer-card {
  background: linear-gradient(135deg, #f0f4ff 0%, #e6e9ff 100%);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
}

.referral-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 15px;
}

.referral-item {
  background: white;
  border-radius: 12px;
  padding: 15px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.affiliate-stats {
  display: flex;
  gap: 20px;
  padding-top: 10px;
  border-top: 1px solid var(--border);
}

.commission-item {
  display: flex;
  justify-content: space-between;
  padding: 15px;
  border-bottom: 1px solid var(--border);
  align-items: flex-start;
}

.commission-info {
  flex: 1;
}

.commission-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 10px;
}

.commission-date {
  font-size: 0.9rem;
  color: var(--gray);
}

.commission-amount {
  font-weight: 500;
  color: var(--success);
}

.commission-poster {
  max-width: 120px;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.1);
}

.reversal-alert {
  background: rgba(227, 52, 47, 0.05);
  border-left: 4px solid var(--danger);
}

.alert-content {
  display: flex;
  gap: 15px;
  align-items: center;
  padding: 15px;
}

.reversal-amount {
  font-size: 1.2rem;
  font-weight: 500;
  color: var(--danger);
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

/* New commission styles */
.poster-upload {
  margin-top: 15px;
}

.upload-label {
  font-weight: 500;
  margin-bottom: 8px;
  color: var(--dark);
}

.preview-container {
  margin-top: 15px;
  border: 1px dashed var(--border);
  border-radius: 8px;
  padding: 15px;
  text-align: center;
}

.poster-preview {
  max-width: 100%;
  max-height: 300px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.add-commission-btn {
  margin-left: 15px;
}

/* Action button colors */
.v-btn--icon.teal {
  background-color: #009688 !important;
  color: white !important;
}

.v-btn--icon.deep-orange {
  background-color: #FF5722 !important;
  color: white !important;
}

/* Hover effects for action buttons */
.v-btn--icon.teal:hover {
  background-color: #00796B !important;
}

.v-btn--icon.deep-orange:hover {
  background-color: #E64A19 !important;
}
.commission-popup {
  position: fixed;
  top: 64px; /* Adjust based on header height */
  right: 20px;
  width: 500px;
  z-index: 1000;
  box-shadow: 0 4px 20px rgba(0,0,0,0.15);
  border-radius: 8px;
}

.commission-popup .v-card {
  border-radius: 8px;
  overflow: hidden;
}
.commission-popup .v-card-header {
  background: var(--primary);
  color: white;
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
@media (max-width: 768px) {
  .commission-popup {
    width: 95%;
    left: 2.5%;
    right: 2.5%;
  }
}

@media (max-width: 960px) {
  .header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .header-actions {
    align-self: flex-end;
    margin-top: 15px;
  }
  
  .card-header > div {
    width: 100%;
  }
  
  .search-field {
    margin-bottom: 15px;
  }
}

@media (max-width: 600px) {
  .stats-indicators {
    flex-direction: column;
    width: 100%;
  }
  
  .stat-indicator {
    flex-direction: row;
    justify-content: space-between;
    min-width: auto;
    padding: 15px;
  }
  
  .card-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .search-field {
    max-width: 100%;
    width: 100%;
  }
  
  .chart-bars {
    gap: 10px;
  }
  
  .add-commission-btn {
    margin-top: 15px;
    margin-left: 0;
    width: 100%;
  }
  
  .commission-item {
    flex-direction: column;
    gap: 15px;
  }
  
  .commission-right {
    flex-direction: row;
    justify-content: space-between;
    width: 100%;
  }
  
  /* Adjust action buttons for mobile */
  .actions {
    flex-direction: column;
    gap: 5px;
  }
  
  .actions .v-btn {
    width: 32px;
    height: 32px;
  }
}

.commission-popup {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0,0,0,0.15);
}

.affiliate-card {
  border-radius: 10px;
  overflow: hidden;
  position: relative;
  transition: transform 0.3s;
}

.affiliate-card:hover {
  transform: translateY(-3px);
}

.blue-light {
  background: linear-gradient(to right, #f5f9ff, #ffffff);
  border-left: 3px solid #1976D2;
}

.green-light {
  background: linear-gradient(to right, #f0fff4, #ffffff);
  border-left: 3px solid #4CAF50;
}

.connection-line {
  position: relative;
  height: 2px;
  margin: 20px 0;
}

.connection-dot {
  position: absolute;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #1976D2;
  top: -4px;
}

.connection-dot.start {
  left: 0;
}

.connection-dot.end {
  right: 0;
  background: #4CAF50;
}

.connection-line-path {
  height: 2px;
  background: linear-gradient(to right, #1976D2, #4CAF50);
}

.commission-viz {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 20px;
}

.commission-arrow {
  animation: pulse-arrow 2s infinite;
}

@keyframes pulse-arrow {
  0% { transform: translateX(0); }
  50% { transform: translateX(5px); }
  100% { transform: translateX(0); }
}

.referrer-info, .affiliate-info {
  flex: 1;
  padding: 10px;
  min-width: 150px;
}

.pulse-glow {
  animation: pulse-glow 2s ease-in-out infinite;
}

@keyframes pulse-glow {
  0% { box-shadow: 0 0 0 0 rgba(66, 133, 244, 0.5); }
  70% { box-shadow: 0 0 0 10px rgba(66, 133, 244, 0); }
  100% { box-shadow: 0 0 0 0 rgba(66, 133, 244, 0); }
}

.elegant-appear-enter-active {
  transition: all 0.3s ease-out;
}
.elegant-appear-leave-active {
  transition: all 0.2s ease-in;
}
.elegant-appear-enter,
.elegant-appear-leave-to {
  transform: translateY(20px);
  opacity: 0;
}


/* Animation alternative de tremblement */
/*
.shake {
  animation: shake-animation 0.5s infinite;
}

@keyframes shake-animation {
  0% { transform: translateX(0); }
  25% { transform: translateX(-3px); }
  75% { transform: translateX(3px); }
  100% { transform: translateX(0); }
}
*/

/* Styles pour le défilement horizontal */
.scrollable-container {
  overflow-x: auto;
  padding-bottom: 16px; 
  width: 100%;
}

.chart-bars {
  display: flex;
  flex-wrap: nowrap;
  min-width: fit-content;
  gap: 20px; 
  align-items: flex-end;
  height: 250px; 
}

.chart-bar {
  min-width: 60px; 
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
}
@media (max-width: 768px) {
  .chart-bars {
    height: 200px;
  }
  .chart-bar {
    min-width: 50px;
  }
}
.commission-selection {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;
}

.selection-header {
  display: flex;
  align-items: center;
  font-weight: 600;
  margin-bottom: 16px;
  font-size: 1.1rem;
  color: #333;
}

.selection-header .v-icon {
  margin-right: 10px;
}

.reversal-alert {
  background-color: #fff8f8;
  border-left: 4px solid #ff5252;
  padding: 12px 16px;
  margin-bottom: 20px;
}

.alert-content {
  display: flex;
  align-items: center;
}

.alert-content .v-icon {
  margin-right: 10px;
  font-size: 24px;
}

.reversal-amount {
  font-weight: 600;
  margin-top: 5px;
}

.commission-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  margin-bottom: 8px;
}

.commission-info {
  flex-grow: 1;
}

.commission-description {
  margin-bottom: 8px;
}

.affiliate-info {
  display: flex;
  align-items: center;
  font-size: 0.85rem;
  color: #666;
}

.affiliate-info .v-icon {
  margin-right: 4px;
}

.commission-date {
  font-size: 0.8rem;
  color: #888;
}

.commission-amount {
  font-weight: bold;
  color: green;
}
/* Styles optimisés mobile */
.mobile-column {
  flex-direction: column;
  align-items: flex-start !important;
}

.mobile-full-width {
  width: 100%;
}

.mobile-search-group {
  width: 100%;
}

.mobile-buttons {
  width: 100%;
  justify-content: flex-end;
}

.mobile-btn {
  min-width: auto !important;
  padding: 0 8px !important;
}

.mobile-item {
  border: 1px solid #eee;
  border-radius: 8px;
}

.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 120px;
}

.scrollable-chart {
  overflow-x: auto;
  padding-bottom: 10px;
}

.chart-bars {
  display: flex;
  min-width: 600px; /* Largeur minimale pour le défilement */
}

.top-affiliates-list .user-info,
.top-affiliates-list .affiliate-commission {
  max-width: 45%;
}

@media (max-width: 600px) {
  .card-title span {
    display: block;
  }
  
  .user-email {
    font-size: 0.75rem;
  }
  
  .bar-label, .bar-amount {
    font-size: 0.7rem;
  }

}


</style>